import { addYears, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  FormItem,
  FormLabel,
  Popover,
  PopoverTrigger,
  FormControl,
  Button,
  cn,
  PopoverContent,
  FormMessage,
  Calendar,
  ButtonProps,
} from 'ui';

export const DateInputField = ({
  value,
  onChange,
  label,
  disabled,
  fromDate,
  size,
  labelClassName,
  readonly = false,
  className,
  closeOnSelect = false,
}: {
  value?: Date;
  onChange: (...event: any[]) => void;
  label: string;
  disabled?: (date: Date) => boolean;
  // The first day the calendar reaches, so its month and year lists start there.
  fromDate?: Date;
  size?: ButtonProps['size'];
  labelClassName?: string;
  readonly?: boolean;
  className?: string;
  closeOnSelect?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Picking closes the calendar, and picking the chosen day again keeps it.
  const select = useCallback(
    (date?: Date) => {
      if (!closeOnSelect) return onChange(date);

      setIsOpen(false);
      if (date) onChange(date);
    },
    [onChange, closeOnSelect],
  );

  return (
    <FormItem className={className}>
      <FormLabel className={labelClassName}>{label}</FormLabel>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger disabled={readonly} asChild>
          <FormControl>
            <Button
              disabled={readonly}
              variant={'outline'}
              className={cn(
                'w-full pl-3 text-left font-normal focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                !value && 'text-muted-foreground',
              )}
              size={size}
            >
              {value ? (
                format(new Date(value), 'PPP', {
                  locale: es,
                })
              ) : (
                <span className={labelClassName}>Selecciona una fecha</span>
              )}
              <CalendarIcon className='ml-auto h-4 w-4 opacity-50' />
            </Button>
          </FormControl>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar
            mode='single'
            locale={es}
            fromDate={fromDate}
            toDate={addYears(new Date(), 3)}
            defaultMonth={value ? new Date(value) : undefined}
            selected={value}
            onSelect={select}
            disabled={
              disabled
                ? disabled
                : (date) => date > new Date() || date < new Date('1900-01-01')
            }
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <FormMessage />
    </FormItem>
  );
};
